import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, analyticsService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
// Deliberately NOT the './shell' barrel: that barrel exports AdminShell, which imports
// this file, so the cycle leaves every token undefined at module-evaluation time and the
// app dies before React mounts. Import the leaves directly.
import {
  AdminModal, Avatar, Btn, DrillList, EmptyLine, Field, Loading, MAGENTA, Pill,
  RING_CONIC, RingLine, TileSpark, inputClass, isoDate, money, relTime,
} from './shell/ui';
import { useAdminShell } from './shell/AdminShellContext';
import { BarList, ColumnChart, fmt, hms } from './analytics/charts';
import UserRecord from './UserRecord';
import WalletMoveModal from './WalletMoveModal';

const RANGES = [7, 14, 30, 90];

/**
 * The person drawer — `?person=<userId>` on any admin route. One request
 * (`GET api/analytics/people/{id}`) carries both halves: what they do on the app
 * (visits, time, pages, device, the daily chart, the area split) and their record
 * (wallet, events, recent activity).
 *
 * It is mounted once by `AdminShell`, so every section gets it by putting `?person=` on
 * the URL — never by rendering a second copy. The writes it offers are the same three
 * USERS offers, each behind its own verb, and each absent without it.
 */
export default function PersonDrawer({ userId, onClose }) {
  const navigate = useNavigate();
  const { can, isSuperadmin } = useUser();
  const { isMobile, toast } = useAdminShell();
  const { showConfirm } = useDialog();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [feeOpen, setFeeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [dayDrill, setDayDrill] = useState(null);
  // FULL RECORD: the drawer widens and the person's record (wallet strip + the five tabs)
  // opens underneath the analytics, same buttons, same look — never a page change.
  const [recordOpen, setRecordOpen] = useState(false);
  useEffect(() => { setRecordOpen(false); }, [userId]);

  /** One day on their chart, opened: every session that day and every step inside it. */
  const openDay = useCallback(async (date) => {
    setDayDrill({ date, loading: true });
    const r = await analyticsService.personDay(userId, date);
    setDayDrill(r.success ? { date, data: r.data } : { date, error: r.error || 'Could not load that day.' });
  }, [userId]);

  const load = useCallback(async () => {
    setError('');
    const r = await analyticsService.person(userId, days);
    if (r.success) setData(r.data);
    else { setData(null); setError(r.error || 'Could not load this person.'); }
  }, [userId, days]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const p = data?.person;
  const s = data?.stats;

  const del = async () => {
    const ok = await showConfirm({
      title: 'DELETE THIS ACCOUNT',
      message: `${p.email} and everything attached to it — profile, wallet, applications and their draft or ended events — goes for good. This cannot be undone.`,
      confirmLabel: 'DELETE USER',
      variant: 'danger',
      theme: 'admin',
    });
    if (!ok) return;
    const r = await adminService.deleteUser(userId);
    if (r.success) { toast?.('ACCOUNT DELETED'); onClose(); }
    else setError(r.error || 'Could not delete this account.');
  };

  const body = (() => {
    if (error) return <div className="px-6 py-8 font-mono text-[12px] text-[#DC2626]">{error}</div>;
    if (!data) return <div className="px-6 py-8"><Loading /></div>;

    const sparks = (data.daily || []).map((d) => d.visits);
    const tiles = [
      { label: 'VISITS', value: fmt(s.visits), sub: `IN ${data.days} DAYS`, spark: sparks },
      { label: 'TIME ON APP', value: hms(s.timeSec), sub: 'ACTIVE, NOT OPEN' },
      { label: 'PAGES / VISIT', value: (s.pagesPerVisit ?? 0).toFixed(1), sub: `${fmt(s.pageViews)} PAGE VIEWS` },
      { label: 'DEVICE', value: (s.device || '—').toUpperCase(), sub: `LAST SEEN ${relTime(p.lastSeen).toUpperCase()}` },
      { label: 'HOSTED', value: fmt(s.hostedCount), sub: 'EVENTS THEY MADE' },
      { label: 'ATTENDED', value: fmt(s.attendedCount), sub: 'SEATS THEY HELD' },
      { label: 'BALANCE', value: money(s.balanceCents), sub: `${money(s.escrowCents)} IN ESCROW` },
      { label: 'SPENT', value: money(s.spentCents), sub: `${money(s.earnedCents)} EARNED` },
    ];

    return (
      <div className="flex flex-col gap-4 px-5 pb-8 pt-4">
        <div className="grid gap-[10px]" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {tiles.map((t) => (
            <div key={t.label} className="flex min-w-0 flex-col gap-[6px] rounded-[22px] bg-[#F7F7F7] p-[16px]">
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">{t.label}</span>
              <span className="truncate font-mono text-[20px] font-extrabold leading-[1.1]">{t.value}</span>
              <span className="truncate font-mono text-[9px] tracking-[0.08em] text-[#9CA3AF]">{t.sub}</span>
              {t.spark && t.spark.length > 0 && <TileSpark values={t.spark.slice(-14)} />}
            </div>
          ))}
        </div>

        <Section title={`VISITS · ${data.days} DAYS`} right="HOVER A DAY FOR WHAT THEY DID">
          {s.noTracking ? (
            <EmptyLine>NO TRACKER HISTORY IN THIS RANGE.</EmptyLine>
          ) : (
            <ColumnChart
              height={140}
              items={(data.daily || []).map((d) => ({
                key: d.date,
                label: shortDay(d.date),
                value: d.visits,
                tip: [['VISITS', fmt(d.visits)], ['PAGE VIEWS', fmt(d.pageViews)], ['TIME', hms(d.timeSec)]],
                breakdownLabel: 'WHAT THEY DID',
                breakdown: (d.did || []).map((x) => ({ label: x.label, value: x.count, display: fmt(x.count) })),
                source: 'analytics_events WHERE user_id = this person GROUP BY day',
              }))}
              onSelect={(it) => openDay(it.key)}
              drill="SEE THAT WHOLE DAY →"
              axisInfo={{
                title: 'VISITS',
                kicker: 'PER DAY',
                desc: 'One visit is one session. A session ends after 30 minutes without a page view or an event.',
                source: 'COUNT(DISTINCT session_id) per day',
              }}
            />
          )}
        </Section>

        <div className="grid gap-[10px]" style={{ gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <Section title="WHERE THEIR TIME GOES">
            <BarList
              items={(data.areas || []).map((a) => ({ label: a.label, count: a.count }))}
              format={(v) => hms(v)}
              source="analytics_events WHERE type = 'engagement' GROUP BY the area of the path"
            />
          </Section>
          <Section title="TOP PAGES">
            <BarList items={(data.topPages || []).map((x) => ({ label: x.label, count: x.count }))} source="analytics_events WHERE type = 'page_view' GROUP BY path" />
          </Section>
          <Section title="HOW THEY GET HERE" right="FIRST TOUCH">
            <BarList
              items={(data.referrers || []).map((r) => ({ label: r.label, count: r.count }))}
              source="the tracker's first touch, per visit — DIRECT is a typed URL, a bookmark or the app itself"
            />
          </Section>
          <Section title="WHERE THEY CAME FROM" right="ONCE, AT SIGNUP">
            <Origin origin={data.origin} onOpen={(href) => { if (href?.startsWith('/admin')) { onClose(); navigate(href); } }} />
          </Section>
        </div>

        <Section title="EVENTS" right={`${(data.events || []).length} SHOWN`}>
          <DrillList
            items={(data.events || []).map((e) => ({
              key: `${e.role}-${e.eventId}`,
              label: e.title,
              sub: `${e.role === 'hosted' ? 'HOSTED' : 'AS A GUEST'} · ${String(e.status).replace(/_/g, ' ').toUpperCase()}${e.dateTime ? ` · ${isoDate(e.dateTime)}` : ''}`,
              display: e.role === 'hosted' ? 'HOST' : money(e.committedCents),
              onClick: can('events', 'view') ? () => { onClose(); navigate(`/admin/events/${e.eventId}`); } : undefined,
            }))}
            empty="NO EVENTS, HOSTED OR ATTENDED."
          />
        </Section>

        <Section title="RECENT ACTIVITY">
          {(data.activity || []).length === 0 ? <EmptyLine>NOTHING ON THE RECORD YET.</EmptyLine> : (
            <ul className="m-0 flex list-none flex-col gap-[2px] p-0">
              {data.activity.map((a, i) => (
                <li key={`${a.at}-${i}`} className="flex items-center gap-3 rounded-[14px] px-2 py-[9px] hover:bg-[#F7F7F7]">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: KIND_COLOR[a.kind] || '#BDBDBD' }} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-mono text-[11px] font-bold uppercase tracking-[0.08em]">{a.label}</span>
                    {a.detail && <span className="truncate font-mono text-[10px] text-[#6B7280]">{a.detail}</span>}
                  </span>
                  <span className="flex-shrink-0 rounded-full bg-[#F2F2F2] px-[10px] py-[4px] font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{relTime(a.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    );
  })();

  return (
    <>
      <div
        className="fixed inset-0 z-[90]"
        style={{ background: 'rgba(0,0,0,.4)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={p ? `${p.name}` : 'Person'}
        data-testid="person-drawer"
        data-record-open={recordOpen ? 'true' : undefined}
        className={`fixed z-[95] flex flex-col overflow-y-auto bg-white transition-[width] duration-300 ${isMobile ? 'inset-0 safe-inset' : `bottom-[10px] right-[10px] top-[10px] ${recordOpen ? 'w-[1180px]' : 'w-[620px]'} max-w-[calc(100%-20px)] rounded-[28px]`}`}
      >
        <header className="relative flex-shrink-0 overflow-hidden bg-[#0A0A0A] px-5 pb-5 pt-[22px] text-white">
          <RingLine />
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-4">
              <span className="relative flex h-[72px] w-[72px] flex-shrink-0 items-center justify-center rounded-full p-[3px]" style={{ background: RING_CONIC }}>
                <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#1F1F1F]">
                  <Avatar url={p?.avatarUrl} name={p?.name} size={66} className="bg-[#1F1F1F] text-white" />
                </span>
                {p?.isOnline && <span className="absolute bottom-[2px] right-[2px] h-[14px] w-[14px] rounded-full border-2 border-[#0A0A0A] bg-[#16A34A]" />}
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: MAGENTA }}>
                  {p?.staffRole ? `${p.staffRole} · STAFF` : 'PERSON'}
                </span>
                <span className="truncate font-mono text-[22px] font-bold leading-[1.15]">{p?.name || '…'}</span>
                <span className="truncate font-mono text-[11px] text-[#BDBDBD]">
                  {[p?.handle ? `@${p.handle}` : null, p?.email, p?.city, p?.joinedAt ? `JOINED ${isoDate(p.joinedAt)}` : null].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#1F1F1F] font-mono text-[14px] font-bold text-white hover:bg-[#2A2A2A]"
            >
              ✕
            </button>
          </div>

          {p && (
            <div className="mt-4 flex flex-wrap gap-[6px]">
              <Pill tone={p.identityVerified ? 'good' : 'neutral'}>{p.identityVerified ? 'VERIFIED' : 'NOT VERIFIED'}</Pill>
              {p.isOnline && <Pill tone="good">ONLINE NOW</Pill>}
              {p.isTest && <Pill tone="warn">DEMO</Pill>}
              {p.isSuperadmin && <Pill tone="magenta">SUPERADMIN</Pill>}
              <Pill tone="dark">{fmt(p.friendCount)} FRIENDS</Pill>
              <Pill tone="dark">{fmt(p.clubCount)} CLUBS</Pill>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-[6px]">
            {p?.portalPath && (
              <a
                href={p.portalPath}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 items-center rounded-full bg-white px-5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-black hover:opacity-85"
              >
                OPEN PORTAL PAGE ↗
              </a>
            )}
            {can('users', 'view') && (
              <Btn variant="quiet" onClick={() => setRecordOpen((v) => !v)} aria-expanded={recordOpen}>{recordOpen ? 'CLOSE RECORD' : 'FULL RECORD'}</Btn>
            )}
            {can('users', 'edit') && <Btn variant="quiet" onClick={() => setFeeOpen(true)}>PORTAL FEE</Btn>}
            {can('wallets', 'adjust') && <Btn variant="quiet" onClick={() => setAdjustOpen('adjust')}>ADJUST WALLET</Btn>}
            {isSuperadmin && <Btn variant="quiet" onClick={() => setAdjustOpen('transfer')}>TRANSFER WALLET</Btn>}
            {isSuperadmin && p?.isStaff && !p?.isSuperadmin && (
              <Btn variant="quiet" onClick={() => { onClose(); navigate(`/admin/staff/${userId}`); }}>OPEN IN STAFF</Btn>
            )}
            {can('users', 'delete') && p && !p.isSuperadmin && <Btn variant="quiet" className="text-[#FF8A8A]" onClick={del}>DELETE USER</Btn>}
            <span className="ml-auto flex gap-[2px] rounded-full bg-[#1F1F1F] p-[3px]">
              {RANGES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  aria-pressed={days === d}
                  className={`h-8 rounded-full px-[11px] font-mono text-[10px] font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${days === d ? 'bg-white text-black' : 'text-[#BDBDBD] hover:text-white'}`}
                >
                  {d}D
                </button>
              ))}
            </span>
          </div>
        </header>

        {body}
        {recordOpen && can('users', 'view') && (
          <section className="mx-5 mb-8 flex-shrink-0 overflow-hidden rounded-[24px] bg-white ring-1 ring-[#F2F2F2]" aria-label="Full record">
            <div className="px-5 pb-2 pt-4 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B7280]">FULL RECORD</div>
            <UserRecord userId={userId} onChanged={load} />
          </section>
        )}
      </aside>

      <AdminModal
        open={!!dayDrill}
        onClose={() => setDayDrill(null)}
        title={dayDrill ? `THEIR DAY · ${shortDay(dayDrill.date)}` : ''}
        kicker={dayDrill?.data
          ? `${fmt(dayDrill.data.visits)} ${dayDrill.data.visits === 1 ? 'visit' : 'visits'} · ${fmt(dayDrill.data.pageViews)} page views · ${hms(dayDrill.data.timeSec)} active. Every step, in the order they took it.`
          : undefined}
        width={580}
      >
        {dayDrill?.loading && <Loading />}
        {dayDrill?.error && <div className="font-mono text-[12px] text-[#DC2626]">{dayDrill.error}</div>}
        {dayDrill?.data && <DayJourney day={dayDrill.data} />}
      </AdminModal>

      {feeOpen && p && <FeeModal person={p} onClose={() => setFeeOpen(false)} onSaved={() => { setFeeOpen(false); toast?.('PORTAL FEE SAVED'); load(); }} />}
      <WalletMoveModal
        person={adjustOpen && p ? { id: userId, name: p.name, email: p.email, handle: p.handle, avatarUrl: p.avatarUrl, balance: data?.stats?.balanceCents ?? 0, escrow: data?.stats?.escrowCents ?? 0 } : null}
        initialMode={adjustOpen || 'adjust'}
        onClose={() => setAdjustOpen(false)}
        onDone={() => load()}
      />
    </>
  );
}

const KIND_COLOR = { money: MAGENTA, event: '#127BFD', visit: '#BDBDBD', account: '#16A34A' };

const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
};

/** "7:12 PM" — the clock on a step, in the reader's own timezone. */
const clock = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' });
};

/**
 * One day of one person's journey: a card per visit — when it started, where it came
 * from, what they were on — and inside it every step in order, with the active seconds
 * spent on each. A page is a magenta dot, anything they DID is a blue one.
 */
function DayJourney({ day }) {
  const sessions = day.sessions || [];
  return (
    <div className="flex flex-col gap-3 pb-2">
      {(day.referrers || []).length > 0 && (
        <div className="rounded-[20px] bg-[#F7F7F7] px-3 py-3">
          <div className="px-2 pb-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">HOW THEY GOT HERE</div>
          <BarList items={day.referrers.map((r) => ({ label: r.label, count: r.count }))} />
        </div>
      )}
      {sessions.length === 0 && <EmptyLine>NOTHING TRACKED THAT DAY.</EmptyLine>}
      {sessions.map((v, i) => (
        <section key={`${v.startedAt}-${i}`} className="rounded-[20px] bg-[#F7F7F7] px-3 py-3">
          <header className="flex flex-wrap items-baseline justify-between gap-2 px-2 pb-[10px]">
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em]">
              VISIT {i + 1} · {clock(v.startedAt)}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF]">
              {[v.referrer, v.device, v.city, `${fmt(v.pageViews)} PAGES`, hms(v.timeSec)].filter(Boolean).join(' · ')}
            </span>
          </header>
          <ol className="m-0 flex list-none flex-col p-0">
            {(v.steps || []).map((st, j) => (
              <li key={`${st.at}-${j}`} className="flex items-center gap-[10px] rounded-[12px] px-2 py-[7px] hover:bg-white">
                <span className="w-[52px] flex-shrink-0 font-mono text-[9px] tracking-[0.06em] text-[#9CA3AF]">{clock(st.at)}</span>
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: st.kind === 'page' ? MAGENTA : '#127BFD' }} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-mono text-[11px] font-semibold">{st.label}</span>
                  {st.detail && <span className="truncate font-mono text-[9px] uppercase tracking-[0.08em] text-[#9CA3AF]">{st.detail}</span>}
                </span>
                {st.secondsHere > 0 && (
                  <span className="flex-shrink-0 rounded-full bg-[#EDEDED] px-[9px] py-[3px] font-mono text-[9px] font-bold tracking-[0.08em] text-[#6B7280]">{hms(st.secondsHere)}</span>
                )}
              </li>
            ))}
          </ol>
          {v.moreSteps > 0 && (
            <div className="px-2 pt-2 font-mono text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF]">+{fmt(v.moreSteps)} MORE STEPS IN THIS VISIT</div>
          )}
        </section>
      ))}
    </div>
  );
}

const SIGNUP_WORDS = {
  email: 'EMAIL + PASSWORD',
  google: 'GOOGLE',
  apple: 'APPLE',
  guest_checkout: 'GUEST CHECKOUT, NEVER CLAIMED',
  system: 'SYSTEM ACCOUNT',
};

/**
 * The account's origin: how they signed up, the invite card that brought them, and the
 * first page their browser ever opened.
 *
 * Every row says WE DON'T KNOW rather than guessing. An account made before Oct 15 2026
 * has none of this, and "DIRECT" would be a claim we cannot support — the difference
 * between "we looked and there was no referrer" and "we never looked" is the whole value
 * of the section, so the empty states are not decoration.
 */
function Origin({ origin, onOpen }) {
  const o = origin || {};
  const unknown = <span className="text-[#9CA3AF]">WE DON&apos;T KNOW</span>;

  const campaign = [o.utmCampaign, o.utmSource, o.utmMedium].filter(Boolean).join(' · ');
  const referrer = o.referrer === '' ? 'DIRECT' : (o.referrerLabel || null);

  const rows = [
    ['SIGNED UP WITH', o.signupMethod ? (SIGNUP_WORDS[o.signupMethod] || String(o.signupMethod).toUpperCase()) : null],
    ['INVITED BY', o.invitedByName
      ? (
        <span>
          {o.invitedByHref && o.invitedByHref.startsWith('/admin') ? (
            <button type="button" onClick={() => onOpen(o.invitedByHref)} className="underline decoration-dotted underline-offset-2 hover:opacity-70">
              {o.invitedByName}
            </button>
          ) : o.invitedByName}
          <span className="text-[#9CA3AF]">{` · CARD ${o.inviteCode?.toUpperCase()}`}</span>
        </span>
      )
      : <span className="text-[#9CA3AF]">NOBODY — NO INVITE CARD</span>],
    ['FIRST CAME FROM', referrer],
    ['CAMPAIGN', campaign || null],
    ['FIRST LANDED ON', o.landingPath],
  ];

  return (
    <dl className="flex flex-col gap-[2px]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex min-w-0 items-baseline justify-between gap-3 rounded-[14px] bg-white px-3 py-[9px]">
          <dt className="shrink-0 font-mono text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF]">{label}</dt>
          <dd className="min-w-0 truncate text-right font-mono text-[11px] font-bold uppercase tracking-[0.04em]">{value || unknown}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A titled block inside the drawer — the drawer's own quiet version of Card. */
function Section({ title, right, children }) {
  return (
    <section className="min-w-0 rounded-[24px] bg-[#F7F7F7] pt-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-3">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em]">{title}</span>
        {right && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF]">{right}</span>}
      </header>
      <div className="px-3 pb-3">{children}</div>
    </section>
  );
}

/** PORTAL FEE — the per-ticket service fee for this one person. `users · edit`. */
function FeeModal({ person, onClose, onSaved }) {
  const [value, setValue] = useState(person.prServiceFeeOverrideCents == null ? '' : (person.prServiceFeeOverrideCents / 100).toFixed(2));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const trimmed = value.trim();
    let cents = null;
    if (trimmed !== '') {
      const dollars = parseFloat(trimmed);
      if (Number.isNaN(dollars) || dollars < 0) { setError('A number that is not negative, or empty to clear it.'); return; }
      cents = Math.round(dollars * 100);
    }
    setBusy(true);
    const r = await adminService.setPrFeeOverride(person.userId, cents);
    setBusy(false);
    if (r.success) onSaved(); else setError(r.error || 'Could not save that.');
  };
  return (
    <AdminModal
      open
      onClose={onClose}
      title="PORTAL SERVICE FEE"
      kicker={`What ${person.name}'s guests pay on every checkout. Empty is the $1 default; 0 waives it. Stripe's own processing fee is never affected.`}
      footer={<><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={save} disabled={busy}>{busy ? 'SAVING…' : 'SAVE FEE'}</Btn></>}
    >
      <Field label="FEE IN DOLLARS" error={error}>
        <input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="1.00" className={inputClass} aria-label="Fee in dollars" />
      </Field>
    </AdminModal>
  );
}

